import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

import OrderDetailsTab from './tabs/OrderDetailsTab';
import PrepressTab from './tabs/PrepressTab';
import EngravingTab from './tabs/EngravingTab';
import ProofingTab from './tabs/ProofingTab';
import BillingTab from './tabs/BillingTab';
import { INITIAL_ORDER_DATA, flattenOrderToForm, buildOrderPayload } from './orderFormSchema';
import { useOrderFormDerived } from './useOrderFormDerived';

const TAB_LABELS = {
  order: 'Order Details',
  prepress: 'Prepress Planning',
  engraving: 'Engraving',
  proofing: 'Proofing',
  billing: 'Billing & Dispatch',
};

const CreateOrderModal = ({ isOpen, onClose, onSuccess, editingOrderId = null }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const isEditing = !!editingOrderId;

  const [colours, setColours] = useState([]);
  const [printingSubstrates, setPrintingSubstrates] = useState([]);
  const [laminationSubstrates, setLaminationSubstrates] = useState([]);
  const [transports, setTransports] = useState([]);
  const [activeDetailTab, setActiveDetailTab] = useState('order');
  const [orderData, setOrderData] = useState({ ...INITIAL_ORDER_DATA });

  // ---- Auto-calculated derived fields ----
  useOrderFormDerived(orderData, setOrderData);

  // ---- Data fetching ----
  const fetchCustomers = useCallback(async () => {
    try { const res = await apiClient.get('/customers'); setCustomers(res.data); }
    catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to fetch customers');
    }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [c, ps, ls, tr] = await Promise.all([
        apiClient.get('/colours'),
        apiClient.get('/printing-substrates'),
        apiClient.get('/lamination-substrates'),
        apiClient.get('/transports'),
      ]);
      setColours(c.data);
      setPrintingSubstrates(ps.data);
      setLaminationSubstrates(ls.data);
      setTransports(tr.data || []);
    } catch (error) { console.error('Failed to fetch master data:', error); }
  }, []);

  const loadOrderForEditing = useCallback(async (orderId) => {
    setLoadingOrder(true);
    try {
      const res = await apiClient.get(`/orders/${orderId}`);
      setSelectedCustomer(res.data.customer_id || res.data.customer_key || '');
      setOrderData(flattenOrderToForm(res.data));
    } catch (error) {
      console.error('Failed to load order for editing:', error);
      toast.error('Failed to load order for editing');
      onClose();
    } finally {
      setLoadingOrder(false);
    }
  }, [onClose]);

  // ---- Effects ----
  useEffect(() => {
    if (!isOpen) return;
    fetchCustomers();
    fetchMasterData();
    setSelectedCustomer('');
    setSelectedCustomerData(null);
    setSelectedMachine('');
    if (editingOrderId) loadOrderForEditing(editingOrderId);
    else setOrderData({ ...INITIAL_ORDER_DATA });
  }, [isOpen, editingOrderId, fetchCustomers, fetchMasterData, loadOrderForEditing]);

  useEffect(() => {
    if (!selectedCustomer) { setSelectedCustomerData(null); return; }
    const c = customers.find(x => x.id === selectedCustomer);
    if (c) setSelectedCustomerData(c);
    else apiClient.get(`/customers/${selectedCustomer}`).then(r => setSelectedCustomerData(r.data)).catch(() => {});
  }, [selectedCustomer, customers]);

  // In edit mode, reverse-lookup the machine from the loaded bore/degree/key_way
  // so the "Select Machine" combobox displays its saved value.
  useEffect(() => {
    if (!editingOrderId || !selectedCustomerData || selectedMachine) return;
    const bore = orderData.bore;
    const degree = orderData.degree;
    if (!bore && !degree) return;
    const m = (selectedCustomerData.bore_details || []).find(x =>
      x.machine_name &&
      String(x.outer_diameter || '') === String(bore || '') &&
      String(x.degree || '') === String(degree || '')
    );
    if (m) setSelectedMachine(m.machine_name);
  }, [editingOrderId, selectedCustomerData, orderData.bore, orderData.degree, selectedMachine]);

  // When user picks a machine, pull its bore/degree/key_way into the spec fields
  useEffect(() => {
    if (!selectedMachine || !selectedCustomerData) return;
    const m = selectedCustomerData.bore_details?.find(x =>
      x.machine_name === selectedMachine || x.machine_name.toLowerCase() === selectedMachine.toLowerCase()
    );
    if (m) {
      setOrderData(prev => ({
        ...prev,
        bore: m.outer_diameter?.toString() || '',
        degree: m.degree?.toString() || '',
        key_way: m.key_way?.toString() || '',
      }));
    }
  }, [selectedMachine, selectedCustomerData]);

  // ---- Handlers ----
  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOrderData(prev => ({ ...prev, [fieldName]: res.data.url }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    }
  };

  const handleSubmit = async () => {
    if (!orderData.job_name) { toast.error('Please fill in manufacturing details to generate Job Name'); return; }
    if (!orderData.circumference || !orderData.length || !orderData.nos_of_cylinders || !orderData.bore || !orderData.degree) {
      toast.error('Please fill all required cylinder specifications');
      return;
    }
    setLoading(true);
    try {
      const payload = buildOrderPayload(orderData, selectedCustomer);
      if (isEditing) {
        await apiClient.put(`/orders/${editingOrderId}`, payload);
        toast.success('Order updated successfully');
      } else {
        await apiClient.post('/orders', payload);
        toast.success('Order created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} order`);
    } finally {
      setLoading(false);
    }
  };

  const getConsigneeOptions = () => {
    if (!selectedCustomerData?.consignees) return [];
    return selectedCustomerData.consignees
      .filter(c => c.consignee_name)
      .map(c => ({ value: c.consignee_name, label: `${c.consignee_name}${c.address ? ` - ${c.address}` : ''}` }));
  };

  const transportOptions = (() => {
    const t = transports.find(tr => tr.customer_id === selectedCustomer);
    return (t?.entries || []).map(e => ({ value: e.transporter_name, label: e.transporter_name }));
  })();

  // ---- Render ----
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="create-order-modal" className="max-w-[98vw] w-full h-[98vh] max-h-[98vh] overflow-y-auto bg-[#dcdcdc] flex flex-col p-4">
        <DialogHeader className="flex-shrink-0 pb-2">
          <DialogTitle className="text-lg font-primary font-semibold">
            {isEditing ? 'Edit Order' : 'Create New Order'}
          </DialogTitle>
        </DialogHeader>

        {loadingOrder ? (
          <div className="py-12 text-center text-slate-600">Loading order data...</div>
        ) : (
          <div className="space-y-3 text-sm flex-1 overflow-y-auto">
            <div className="flex gap-0 border-b-2 border-[#a0a0a0] flex-shrink-0">
              {Object.entries(TAB_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveDetailTab(key)}
                  className={`px-4 py-2 text-xs font-bold capitalize transition-colors ${activeDetailTab === key ? 'bg-[#a0a0a0] text-slate-900 border-t-2 border-x border-[#a0a0a0]' : 'bg-[#c8c8c8] text-slate-700 hover:bg-[#b8b8b8]'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeDetailTab === 'order' && (
              <OrderDetailsTab
                orderData={orderData} setOrderData={setOrderData}
                customers={customers}
                selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
                selectedCustomerData={selectedCustomerData}
                selectedMachine={selectedMachine} setSelectedMachine={setSelectedMachine}
                getConsigneeOptions={getConsigneeOptions}
              />
            )}
            {activeDetailTab === 'prepress' && (
              <PrepressTab orderData={orderData} setOrderData={setOrderData} onFileUpload={handleFileUpload} />
            )}
            {activeDetailTab === 'engraving' && (
              <EngravingTab orderData={orderData} setOrderData={setOrderData} />
            )}
            {activeDetailTab === 'proofing' && (
              <ProofingTab
                orderData={orderData} setOrderData={setOrderData}
                printingSubstrates={printingSubstrates}
                laminationSubstrates={laminationSubstrates}
                colours={colours}
              />
            )}
            {activeDetailTab === 'billing' && (
              <BillingTab
                orderData={orderData} setOrderData={setOrderData}
                transportOptions={transportOptions}
              />
            )}

            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-xs text-slate-900 mb-2">Order Summary</h4>
              <div className="grid grid-cols-4 gap-1 text-xs">
                <p><span className="text-slate-600">Customer:</span> {selectedCustomerData?.customer_name || selectedCustomerData?.addresses?.[0]?.customer_name_child || selectedCustomerData?.parent_customer_name || '-'}</p>
                <p className="col-span-2"><span className="text-slate-600">Job Name:</span> <span className="font-semibold">{orderData.job_name || '-'}</span></p>
                <p><span className="text-slate-600">Cylinders:</span> {orderData.nos_of_cylinders || '-'}</p>
                <p><span className="text-slate-600">Circumference:</span> {orderData.circumference ? `${orderData.circumference} mm` : '-'}</p>
                <p><span className="text-slate-600">Length:</span> {orderData.length ? `${orderData.length} mm` : '-'}</p>
                <p><span className="text-slate-600">Bore:</span> {orderData.bore ? `${orderData.bore} mm` : '-'}</p>
                <p><span className="text-slate-600">Degree:</span> {orderData.degree ? `${orderData.degree}\u00B0` : '-'}</p>
                <p><span className="text-slate-600">Key Way:</span> {orderData.key_way || '-'}</p>
                <p><span className="text-slate-600">BS Type:</span> {orderData.new_baseshell ? `BS: ${orderData.new_baseshell}` : ''}{orderData.pbs ? ` PBS: ${orderData.pbs}` : ''}{!orderData.new_baseshell && !orderData.pbs ? '-' : ''}</p>
              </div>
            </div>

            <div className="flex gap-3 pt-3 border-t">
              <Button variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || !selectedCustomer || !orderData.job_name}
                className="flex-1 h-9 text-sm"
              >
                {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Order' : 'Create Order')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrderModal;
